// @ts-nocheck
/**
 * Header export for the Databook card. The trigger opens a small pop-up (the
 * Full Statements export's panel, `--below` variant) with the shared
 * "Include cell references" switch, on by default, and the download button.
 * It downloads the client's own workbook from
 * POST /agent/sessions/{parentId}/export-databook. With references on, every
 * source sheet is copied in and the Source TB numbers link to it; when the
 * source workbooks are gone (404) the switch turns off and the next click
 * downloads values only.
 *
 * Mounted by FinalMappingCardUnified in place of the Full Statements popover
 * (its `ExportControl` prop), so it takes the same props. What the export
 * says about the workbook it built (the period window, the months of data,
 * months left out, accounts with no line in the template, and the server's
 * notes) and any failure are reported through `onNotice`, which the card
 * renders in the strip under its header.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, Download, Loader2 } from 'lucide-react';

import {
  downloadDatabookWorkbook,
  SourceFileMissingError,
} from '../../utils/exportExcel';
import CellReferencesToggle from './CellReferencesToggle';
import './DatabookAgent.css';

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Short facts, shown on one line.
const exportedFacts = (exported) => {
  const facts = [];
  if (exported.periodWindow) {
    facts.push({ text: `Exported ${exported.periodWindow}` });
  }
  if (exported.months != null) {
    facts.push({ text: `${count(exported.months, 'month', 'months')} of data` });
  }
  // The server's notes name the months left out (and when), so the bare
  // count is only shown when no notes came back.
  if (exported.droppedMonths > 0 && !exported.notes) {
    facts.push({
      tone: 'warn',
      text: count(exported.droppedMonths, 'month left out', 'months left out'),
    });
  }
  if (exported.unmapped > 0) {
    facts.push({
      tone: 'warn',
      text: `${count(exported.unmapped, 'account has', 'accounts have')} no line in the template`,
    });
  }
  return facts;
};

const ExportNotice = ({ facts = [], notes = [] }) => (
  <span className="dbx-notice">
    {facts.length > 0 && (
      <span className="dbx-notice-facts">
        {facts.map((fact, i) => (
          <span
            key={i}
            className={`dbx-notice-part ${fact.tone ? `is-${fact.tone}` : ''}`}
          >
            {fact.text}
          </span>
        ))}
      </span>
    )}
    {notes.map((note, i) => (
      <span key={i} className="dbx-notice-part is-warn">
        {note}
      </span>
    ))}
  </span>
);

const DatabookExportButton = ({ parentId, hasPnl = true, hasBs = true, onNotice }) => {
  // Same open/mounted/visible machine as UnifiedExportButton, so the panel
  // animates in and out.
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [triggerPos, setTriggerPos] = useState({ top: 0, left: 0, notch: null });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [enableReferencing, setEnableReferencing] = useState(true);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  // The client's workbook is fed by both statements; the server refuses the
  // export (409) until both are mapped.
  const ready = Boolean(parentId) && hasPnl && hasBs;

  // Dismiss on outside click / Escape.
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onPointer = (e) => {
      const t = e.target;
      if (panelRef.current && panelRef.current.contains(t)) return;
      if (triggerRef.current && triggerRef.current.contains(t)) return;
      setPanelOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [panelOpen]);

  useEffect(() => {
    if (panelOpen) {
      setPanelMounted(true);
      onNotice?.(null);
      setDownloadError(null);
      const r = requestAnimationFrame(() => setPanelVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setPanelVisible(false);
    const t = setTimeout(() => setPanelMounted(false), 240);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpen]);

  // Pin the panel below the trigger, right-aligned to it, clamped to the
  // viewport (see UnifiedExportButton).
  useLayoutEffect(() => {
    if (!panelMounted) return undefined;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 16;
      const panelW = panelRef.current ? panelRef.current.offsetWidth : 0;
      const top = r.bottom + 12;
      let left = r.right;
      let notch = null;
      if (panelW > 0) {
        left = Math.max(left, margin + panelW);
        left = Math.min(left, window.innerWidth - margin);
        notch = Math.min(Math.max(left - (r.left + r.width / 2), 18), panelW - 18);
      }
      setTriggerPos((prev) =>
        prev.top === top && prev.left === left && prev.notch === notch
          ? prev
          : { top, left, notch },
      );
    };
    update();
    const ro =
      typeof ResizeObserver !== 'undefined' && panelRef.current
        ? new ResizeObserver(update)
        : null;
    if (ro) ro.observe(panelRef.current);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [panelMounted]);

  const handleDownload = async () => {
    if (!ready || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const exported = await downloadDatabookWorkbook(parentId, { enableReferencing });
      setPanelOpen(false);
      const facts = exportedFacts(exported);
      const notes = exported.notes || [];
      if (facts.length > 0 || notes.length > 0) {
        onNotice?.(<ExportNotice facts={facts} notes={notes} />);
      }
    } catch (err) {
      if (err instanceof SourceFileMissingError) {
        // Turn references off so the next click downloads values only.
        setEnableReferencing(false);
        setDownloadError(
          'Source files are no longer available, so cell references are turned off. Click Download again to export without them.',
        );
      } else {
        // The server's detail is written for the user; show it as is.
        setDownloadError(err.message || 'Download failed');
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`fmc-export-wrap ${panelOpen ? 'is-open' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`fmc-btn fmc-btn-primary fmc-export-trigger ${downloading ? 'fmc-btn-loading' : ''}`}
        onClick={() => setPanelOpen((o) => !o)}
        disabled={!ready || downloading}
        title={ready ? undefined : 'The databook needs both statements.'}
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
      >
        {downloading ? (
          <Loader2 size={13} className="fmc-spin" />
        ) : (
          <Download size={13} />
        )}
        <span className="fmc-btn-label">
          {downloading ? 'Generating…' : 'Download databook'}
        </span>
        <ChevronDown size={13} className="fmc-export-trigger-chevron" aria-hidden />
        {downloading && <span className="fmc-btn-shimmer" aria-hidden />}
      </button>

      {panelMounted && (
        <div
          className="fmc-export-anchor fmc-export-anchor--below"
          style={{ top: triggerPos.top, left: triggerPos.left }}
        >
          <div
            ref={panelRef}
            className={`fmc-export-panel fmc-export-panel--below ${panelVisible ? 'is-open' : ''}`}
            role="dialog"
            aria-label="Download options"
            style={
              triggerPos.notch != null
                ? { ['--fmc-export-notch-right' as any]: `${triggerPos.notch}px` }
                : undefined
            }
          >
            <div className="fmc-export-toggle-row dbx-export-toggle-row">
              <CellReferencesToggle
                checked={enableReferencing}
                onChange={setEnableReferencing}
                disabled={downloading}
                hint="When on, the downloaded .xlsx includes a copy of each source sheet and links populated cells back to it via formulas."
              />
            </div>
            <div className="fmc-export-note">
              {enableReferencing
                ? 'Each Source TB number links to its cell in the copied source sheets.'
                : 'Exporting without cell links: values only.'}
            </div>
            <button
              type="button"
              className={`fmc-export-confirm ${downloading ? 'is-loading' : ''}`}
              onClick={handleDownload}
              disabled={downloading || !ready}
            >
              {downloading ? (
                <Loader2 size={14} className="fmc-spin" />
              ) : (
                <Download size={14} />
              )}
              <span>{downloading ? 'Generating…' : 'Download databook'}</span>
              {downloading && <span className="fmc-btn-shimmer" aria-hidden />}
            </button>
            {downloadError && (
              <span className="fmc-export-error" title={downloadError}>
                {downloadError}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabookExportButton;
